import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { withRouter } from 'react-router'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { computed, extendObservable } from 'mobx'

import ArrowBackIcon from '@components/Icon/ArrowBack'
import DownloadIcon from '@components/Icon/Download'
import NotificationIcon from '@components/Icon/Notification'
import ConfirmIcon from '@components/Icon/Confirm'
import AddIcon from '@components/Icon/Add'
import BlockIcon from '@components/Icon/Block'
import EditIcon from '@components/Icon/Edit'
import DeleteIcon from '@components/Icon/Delete'
import MailIcon from '@components/Icon/Mail'
import Action from '@components/Action'
import ACL from '@components/ACL'
import { showDialog, isCancellationError } from '@components/Dialog'

import { capi, invoiceApi, clientContactApi } from '@atheneum/api'

class Actions extends Component {
  static propTypes = {
    modalStore: PropTypes.object,
    growlStore: PropTypes.object,
    history: PropTypes.object,
    invoiceStore: PropTypes.object,
    invoice: PropTypes.object,
    match: PropTypes.object,
    staticData: PropTypes.object,
    isEditingDisabled: PropTypes.bool
  }

  constructor(props) {
    super(props)

    const { staticData, invoice } = props
    const { INVOICE_STATUSES } = staticData.CONST
    extendObservable(this, {
      showRequestApprovalAction: computed(
        () => invoice.invoiceStatusId === INVOICE_STATUSES.DRAFT
      ),
      showApproveAction: computed(() => {
        return (
          invoice.invoiceStatusId === INVOICE_STATUSES.DRAFT ||
          invoice.invoiceStatusId === INVOICE_STATUSES.WAITING_FOR_APPROVAL
        )
      }),
      showDisapproveAction: computed(
        () =>
          invoice.invoiceStatusId === INVOICE_STATUSES.WAITING_FOR_APPROVAL ||
          invoice.invoiceStatusId === INVOICE_STATUSES.APPROVED
      ),
      showDeleteAction: computed(() => !props.isEditingDisabled),
      showCreatedAction: computed(
        () => invoice.invoiceStatusId === INVOICE_STATUSES.APPROVED
      ),
      showSendInvoiceAction: computed(
        () => invoice.invoiceStatusId >= INVOICE_STATUSES.CREATED
      ),
      showCreateCreditNoteAction: computed(() => {
        if (invoice.isCreditNote) return false
        return (
          invoice.invoiceStatusId >= INVOICE_STATUSES.CREATED &&
          invoice.invoiceStatusId !== INVOICE_STATUSES.CREDITED
        )
      })
    })
  }

  handleEditInvoice = () => {
    const { invoice } = this.props

    return this.props.modalStore.show({
      type: 'INVOICE_FORM',
      props: {
        title: 'Edit invoice',
        cancelText: 'Cancel',
        okText: 'Save changes',
        isEdit: true,
        invoice,
        invoiceType: invoice.invoiceTypeId,
        isCreditNote: invoice.isCreditNote,
        contract: invoice.contract,
        project: invoice.project
      }
    })
  }

  handleDeleteInvoice = () => {
    const { invoiceStore, invoice, growlStore } = this.props
    const url = this.generateBackLink()
    const text =
      !!invoiceStore.invoice.fees && invoiceStore.invoice.fees.length > 0
        ? 'Fees associated with this invoice will be unallocated and available for allocation to another invoice.'
        : null

    return showDialog({
      title: 'Delete this invoice?',
      text,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    })
      .then(async () => {
        if (invoice) {
          growlStore.showSuccess('Checking invoice id ...')

          const capiInvoice = await capi.checkInvoice({
            invoiceId: invoice.id
          })

          if (capiInvoice) {
            await capi.updateInvoiceStatus({
              invoiceId: invoice.id,
              status: 'deleted'
            })
          }
        }

        return invoiceStore
          .delete(invoice.id)
          .then(res => {
            this.props.history.replace(url)
          })
          .catch(error => growlStore.showError(error))

      })
      .catch(error => {
        if (isCancellationError(error)) return
        growlStore.showError(error)
      })
  }

  handleApprovalRequest = () => {
    const { invoiceStore, growlStore, invoice } = this.props

    return showDialog({
      title: 'Request approval?',
      text: `Are you sure you want to request approval for ${invoice.name}`,
      type: 'info',
      showCancelButton: true,
      confirmButtonText: 'Request approval'
    })
      .then(async () => {
        if (invoice) {
          growlStore.showSuccess('Checking invoice id ...')
          const capiInvoice = await capi.checkInvoice({
            invoiceId: invoice.id
          })

          if (capiInvoice) {

            await capi.updateInvoiceStatus({
              invoiceId: invoice.id,
              status: 'awaiting_approval'
            })
          }
        }

        await invoiceStore.updateToRequestApprovalStatus(invoice)
        growlStore.showSuccess(`Request approval for invoice ${invoice.name}`)
        return invoiceStore.get(invoice.id)
      })
      .catch(error => {
        if (isCancellationError(error)) return // ignore
        growlStore.showError(error)
      })
  }

  handleApproval = () => {
    const { invoiceStore, growlStore, invoice } = this.props

    return showDialog({
      title: 'Approve invoice?',
      text: `Are you sure you want to approve ${invoice.name}`,
      type: 'info',
      showCancelButton: true,
      confirmButtonText: 'Approve'
    })
      .then(async () => {
        invoiceStore.loadingSingle = true
        if (invoice) {
          growlStore.showSuccess('Checking invoice id ...')
          const capiInvoice = await capi.checkInvoice({
            invoiceId: invoice.id
          })

          if (capiInvoice) {
            await capi.updateInvoiceStatus({
              invoiceId: invoice.id,
              status: 'approved'
            })
          }
        }

        await invoiceStore.updateToApproveStatus(invoice)
        growlStore.showSuccess(`Invoice ${invoice.name} approved`)
        return invoiceStore.get(invoice.id)
      })
      .catch(error => {
        if (isCancellationError(error)) return
        growlStore.showError(error)
      })
  }

  handleDisapproval = () => {
    const { modalStore, invoice } = this.props

    modalStore.show({
      type: 'DISAPPROVE_INVOICE_MODAL',
      props: {
        invoice
      }
    })
  }

  handleCreated = () => {
    const { modalStore, invoice } = this.props

    modalStore.show({
      type: 'SET_INVOICE_CREATED_STATUS_MODAL',
      props: {
        invoice
      }
    })
  }

  handleSendInvoice = async () => {
    const { modalStore, invoice } = this.props
    const { parentAccountId } = invoice.invoicingEntity

    const capiInvoice = await capi.checkInvoice({
      invoiceId: invoice.id
    })

    if (capiInvoice?.call_only_invoice && !invoice.isCreditNote) {
      this.props.growlStore.showSuccess(`Sending Invoice through ENS API`)
      try {
        await invoiceApi.publishInvoiceSent(invoice.id)
        this.props.growlStore.showSuccess(`Successfully sent invoice through ENS API`)
      } catch (error) {
        this.props.growlStore.showError(`Failed to send invoice through ENS API`)
        console.error(error)
      }

      return
    }

    let group = 'send_invoice'

    if (invoice.isCreditNote) {
      group = 'send_credit_note'
    }

    const loadToOptions = query =>
      clientContactApi.typeaheadSearch(parentAccountId, query, true).then(results =>
        results.map(clientContact => {
          clientContact.name = clientContact.fullName
          return clientContact
        })
      )
    modalStore.show({
      type: 'EMAIL_TEMPLATE_SEND',
      props: {
        title: `Send ${invoice.isCreditNote ? 'credit note' : 'invoice'}`,
        group,
        loadTemplateUrl: `/api/invoice/${invoice.id}/send-invoice`,
        sendTemplateUrl: `/api/invoice/${invoice.id}/send-invoice`,
        onAfterSend: () => {
          const entityType = invoice.isCreditNote ? 'Credit note' : 'Invoice'
          this.props.modalStore.close()
          return this.props.growlStore.showSuccess(`${entityType} sent`)
        },
        loadToOptions,
        showAttachments: true
      }
    })
  }

  handleCreateCreditNote = () => {
    const { modalStore, invoice } = this.props

    return modalStore.show({
      type: 'INVOICE_FORM',
      props: {
        title: 'Create credit note',
        cancelText: 'Cancel',
        okText: 'Create credit note',
        isEdit: false,
        invoice,
        invoiceType: invoice.invoiceTypeId,
        isCreditNote: true,
        contract: invoice.contract,
        project: invoice.project
      }
    })
  }

  generateBackLink = () => {
    const { match } = this.props
    return match.url.substr(0, match.url.lastIndexOf('/'))
  }

  render() {
    const { invoice, invoiceStore } = this.props
    const token = localStorage.getItem('ngStorage-accessToken')
    const exportLink = '/api/invoice/' + invoice.id + '/export?token=' + token
    const backLink = this.generateBackLink()

    return (
      <div className='actions-wrapper actions-wrapper__noTop actions-wrapper--space-between clearfix'>
        <ul className='actions left-list'>
          <Link to={backLink}>
            <Action>
              <li>
                <ArrowBackIcon />
                <span>Invoice list</span>
              </li>
            </Action>
          </Link>
        </ul>

        <ul className='actions right-list'>
          {(!!invoiceStore.capiInvoiceStatus && invoiceStore.isCapiCallOnlyInvoice) && (
            <li>
              <span>ENS Invoice status: <strong className='ens-invoice-status'>{this.props.invoiceStore.capiInvoiceStatus.split('_').join(' ')}</strong></span>
            </li>
          )
          }
          {this.showRequestApprovalAction && (
            <Action alignRight>
              <li
                onClick={this.handleApprovalRequest}
                data-cy='request-approval-btn'
              >
                <NotificationIcon />
                <span>Request approval</span>
              </li>
            </Action>
          )}

          <ACL
            action
            inline
            roles={[
              'FeeManagement',
              'Accounting',
              'ProjectManagement',
              'Admin'
            ]}
          >
            {this.showApproveAction && (
              <ACL
                roles={[
                  'Accounting',
                  'Admin',
                  'ProjectManagement',
                  'AdvancedReporting'
                ]}
                action
              >
                <Action alignRight>
                  <li
                    onClick={this.handleApproval}
                    data-cy='approve-invoice-btn'
                  >
                    <ConfirmIcon />
                    <span>Approve</span>
                  </li>
                </Action>
              </ACL>
            )}

            {this.showDisapproveAction && (
              <ACL
                roles={[
                  'Accounting',
                  'Admin',
                  'ProjectManagement',
                  'AdvancedReporting'
                ]}
                action
              >
                <Action alignRight>
                  <li
                    onClick={this.handleDisapproval}
                    data-cy='disapprove-invoice-btn'
                  >
                    <BlockIcon />
                    <span>Disapprove</span>
                  </li>
                </Action>
              </ACL>
            )}

            {this.showCreatedAction && (
              <ACL roles={['Accounting', 'Admin']} action>
                <Action alignRight>
                  <li onClick={this.handleCreated} data-cy='create-invoice-btn'>
                    <AddIcon />
                    <span>Create</span>
                  </li>
                </Action>
              </ACL>
            )}

            {this.showSendInvoiceAction && (
              <ACL roles={['Accounting', 'Admin']} action>
                <Action alignRight>
                  <li onClick={this.handleSendInvoice}>
                    {
                      !this.props.invoiceStore.isFreeCallInvoice && (
                        this.props.invoiceStore.isCapiCallOnlyInvoice
                          ? <span>Send Invoice to ENS</span>
                          : (
                            <><MailIcon />  <span>
                              {invoice.isInvoiceSent
                                ? 'Resend invoice'
                                : 'Send invoice'}
                            </span></>
                          )
                      )}
                  </li>
                </Action>
              </ACL>
            )}

            {this.showCreateCreditNoteAction && (
              <ACL roles={['Accounting', 'Admin']} action>
                <Action alignRight>
                  <li
                    data-cy='credit-note-btn'
                    onClick={this.handleCreateCreditNote}
                  >
                    <EditIcon />
                    <span>Credit note</span>
                  </li>
                </Action>
              </ACL>
            )}
          </ACL>

          <Action alignRight>
            <li>
              <a href={exportLink} download>
                <DownloadIcon />
                <span>Export</span>
              </a>
            </li>
          </Action>
          {this.showDeleteAction && (
            <ACL roles={['Accounting', 'Admin']} action>
              <Action alignRight>
                <li onClick={this.handleDeleteInvoice}>
                  <DeleteIcon />
                  <span>Delete</span>
                </li>
              </Action>
            </ACL>
          )}
          <ACL roles={['Accounting', 'Admin']} action>
            <Action alignRight>
              <li onClick={this.handleEditInvoice} data-cy='edit-invoice-btn'>
                <EditIcon />
                <span>Edit</span>
              </li>
            </Action>
          </ACL>
        </ul>
      </div>
    )
  }
}

export default withRouter(
  inject(
    'staticData',
    'growlStore',
    'modalStore',
    'invoiceStore'
  )(observer(Actions))
)
